'use client';

import { Box } from '@mui/material';
import type * as layout from '@ocodelib/compose-layout';
import { createColorVariables } from '@repo-foundation/util/mui-theme-generate-colors';
import type { IAppConfig } from '@repo/js-basic-app';
import { PLUGIN_KEYS, type JsBasicAppType } from '@repo/js-basic-app';
import type { IProjectLoader, ProjectFile } from '@repo/project-interface';
import dynamic from 'next/dynamic';
import type { CSSProperties } from 'react';
import { useMemo } from 'react';
import { config } from '../../config';
import { CUSTOM_PALETTES, JS_PALETTE } from '../../theme/custom-palette';
import { WorkspaceTopbar } from './components/WorkspaceTopbar';

const JsBasicAppUi = dynamic(() => import('@repo/js-basic-app').then((m) => m.JsBasicAppUi), {
  ssr: false,
});

interface Props {
  previewMode?: boolean;
  onLoadApp?: (app: JsBasicAppType | null) => void;
  newProjectLoader?: IProjectLoader<ProjectFile[]>;
  initialProjectLoader?: IProjectLoader<ProjectFile[]>;
}

export function JsBasicAppLoader(props: Props) {
  const { onLoadApp, previewMode = false, newProjectLoader, initialProjectLoader } = props;
  const style = useMemo(
    () =>
      ({
        ...createColorVariables('primary', JS_PALETTE),
        ...createColorVariables('secondary', CUSTOM_PALETTES.lightBlue),
      }) as CSSProperties,
    [],
  );
  const appConfig = useMemo<IAppConfig>(
    () => ({
      apiBaseURL: config.apiBaseURL,
      datiBaseURL: config.datiBaseURL,
      previewMode,
      newProjectLoader,
      initialProjectLoader,
    }),
    [previewMode, newProjectLoader, initialProjectLoader],
  );

  return (
    <Box style={style}>
      <JsBasicAppUi
        onLayoutBuild={previewMode ? undefined : overrideLayout}
        appConfig={appConfig}
        onLoadApp={onLoadApp}
      />
    </Box>
  );
}

function overrideLayout(layoutManager: layout.ComposeLayoutManager) {
  function reg(key: string, component: React.ReactElement) {
    return layoutManager.registerPlugin(key, component);
  }
  reg(PLUGIN_KEYS.root.topBar, <WorkspaceTopbar />);
}
